import { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Check, Copy } from 'lucide-react';
import { notify } from './BalmartToaster';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'balmart_newsletter_state';
const SHOW_DELAY_MS = 35000; // 35s on site — respect the user before nagging

export default function NewsletterPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null); // { code, discount_percent }

  useEffect(() => {
    let state;
    try { state = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; } catch { state = {}; }
    if (state.dismissed || state.subscribed) return;
    const t = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    setOpen(false);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ dismissed: true, at: Date.now() })); } catch {}
  };

  const submit = async (e) => {
    e.preventDefault();
    const addr = email.trim().toLowerCase();
    if (!addr || !addr.includes('@')) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, { email: addr });
      setSuccess(res.data);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ subscribed: true, at: Date.now() })); } catch {}
    } catch (err) {
      notify.error('Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="newsletter-popup"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
      onClick={dismiss}
    >
      <div
        className="relative w-full max-w-md bg-[#FFFBF4] p-10 sm:p-12"
        onClick={e => e.stopPropagation()}
        style={{ border: '1px solid rgba(0,0,0,0.1)' }}
      >
        <button
          data-testid="newsletter-close"
          onClick={dismiss}
          aria-label="Fermer"
          className="absolute top-4 right-4 p-2 text-black/40 hover:text-black transition-colors"
        >
          <X size={18} />
        </button>

        {!success ? (
          <>
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold mb-3" style={{ color: '#B80000' }}>
              Offre de bienvenue
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl font-light leading-tight mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              <span style={{ color: '#B80000' }}>-10%</span> sur votre<br/>première commande
            </h2>
            <p className="text-sm text-black/60 leading-relaxed mb-6">
              Rejoignez notre cercle privé et recevez un code exclusif, ainsi que nos nouveautés et rituels beauté en avant-première.
            </p>
            <form onSubmit={submit} data-testid="newsletter-form" className="space-y-4">
              <input
                data-testid="newsletter-email-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="votre@email.com"
                required
                className="bg-transparent border-b border-black/20 focus:border-black outline-none py-3 w-full text-sm transition-colors"
              />
              <button
                data-testid="newsletter-submit"
                type="submit"
                disabled={loading}
                className="btn-primary w-full disabled:opacity-50"
              >
                {loading ? 'Envoi…' : 'Recevoir mes -10%'}
              </button>
            </form>
            <p className="text-[10px] uppercase tracking-[0.15em] text-black/30 mt-4 text-center">
              Pas de spam · désabonnement en un clic
            </p>
          </>
        ) : (
          <div className="text-center" data-testid="newsletter-success">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: '#B80000' }}>
              <Check size={26} className="text-white" strokeWidth={2} />
            </div>
            <h2 className="font-serif text-3xl font-light mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Bienvenue dans le cercle
            </h2>
            <p className="text-sm text-black/60 mb-6">
              Votre code a aussi été envoyé par email.
            </p>
            <div className="p-5 mb-5" style={{ backgroundColor: 'rgba(184,0,0,0.06)', border: '1px solid rgba(184,0,0,0.2)' }}>
              <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-black/50 mb-2">Votre code bienvenue</p>
              <p className="font-mono text-2xl font-bold tracking-[4px]" style={{ color: '#B80000' }}>{success.code}</p>
              <p className="text-[11px] text-black/40 mt-2">-{success.discount_percent}% valable 30 jours</p>
            </div>
            <button
              onClick={() => {
                navigator.clipboard?.writeText(success.code);
                notify.success('Code copié');
              }}
              data-testid="copy-welcome-code"
              className="text-xs uppercase tracking-[0.2em] font-bold text-black/50 hover:text-black transition-colors inline-flex items-center gap-2"
            >
              <Copy size={12} /> Copier le code
            </button>
            <button
              onClick={dismiss}
              className="block w-full mt-6 btn-primary"
              data-testid="newsletter-continue"
            >
              Commencer mes achats
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
